import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { t } from "../utils/translations";
import { track } from "../utils/analytics";
import styles from "./NavBar.module.css";

function NavBar() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language, toggleLanguage, isEnglish } = useLanguage();
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  const getUserInitial = () => {
    if (user?.given_name) {
      return user.given_name.charAt(0).toUpperCase();
    }
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    return 'M';
  };

  const handleLogout = () => {
    track('Clicked Logout');
    setProfileOpen(false);
    logout();
    navigate('/');
  };

  return (
    <div 
      className="open-sans-navbar"
      style={{
        width: '100%',
        paddingLeft: '2.5%',
        paddingRight: '2.5%',
        paddingTop: '0.75%',
        paddingBottom: '0.75%',
        /* Gradient: #E5FFD0 (left) → #2EC974 (right) */
        background: 'linear-gradient(90deg, #E5FFD0 0%, #2EC974 100%)',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
        borderBottom: '1px solid rgba(46, 201, 116, 0.5)',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '0.5%',
        display: 'flex',
        height: 'auto',
        boxSizing: 'border-box'
      }}
    >
      <div style={{flex: '1 1 0', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex'}}>
        <div
          onClick={() => navigate("/")}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <img
            src="/assets/KumuConnectLogos/Logo_Long_GreenonLight.png"
            alt={t('kumu_connect', language)}
            style={{
              height: '2.6rem',
              width: 'auto',
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>

        {/* Beta Testing + Creations Left: "BETA TESTING:" [20] "CREATIONS LEFT" – #C2F5FF, dark green border, slightly narrower */}
        <div 
          data-property-1="beta" 
          style={{
            marginLeft: '1.75rem',
            paddingLeft: '0.5rem', 
            paddingRight: '0.5rem', 
            paddingTop: '0.25rem', 
            paddingBottom: '.25rem', 
            background: '#C2F5FF', 
            border: '2px solid #2EC974',
            borderRadius: 8, 
            justifyContent: 'center', 
            alignItems: 'center', 
            display: 'flex',
            flexShrink: 0,
            gap: '0.35rem'
          }}
        >
          <span style={{
            color: '#5F6B92', 
            fontSize: '0.8rem', 
            fontFamily: 'Open Sans', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap'
          }}>
            BETA TESTING:
          </span>
          <span style={{
            minWidth: '1rem',
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem',
            paddingTop: '0.2rem',
            paddingBottom: '0.2rem',
            background: '#FFFFFF',
            border: '1px solid #A6D9F7',
            borderRadius: 6,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.06)',
            color: '#5F6B92',
            fontSize: '0.85rem',
            fontFamily: 'Open Sans',
            fontWeight: '600',
            textAlign: 'center',
            lineHeight: 1.0
          }}>
            {user && (user.generations_remaining !== undefined && user.generations_remaining !== null)
              ? user.generations_remaining
              : '--'}
          </span>
          <span style={{
            color: '#5F6B92', 
            fontSize: '0.75rem', 
            fontFamily: 'Open Sans', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            letterSpacing: '0.02em',
            whiteSpace: 'nowrap'
          }}>
            {String(t('creations_left', language)).toUpperCase()}
          </span>
        </div>
      </div>

      <div style={{justifyContent: 'flex-end', alignItems: 'center', gap: '0.875rem', display: 'flex', flexShrink: 0}}>
        {/* Language Toggle - Ōlelo / English, surrounding area #2EC974 */}
        <div 
          data-toggle="On" 
          data-type="Text" 
          onClick={toggleLanguage}
          style={{
            position: 'relative',
            padding: '0.2rem',
            background: '#2EC974',
            boxShadow: 'inset 0 2px 2px rgba(0,0,0,0.2)',
            borderRadius: 12,
            outline: '1px solid rgba(0,0,0,0.08)',
            outlineOffset: '-1px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            height: '2.1rem',
            minWidth: '11rem'
          }}
        >
          {/* Sliding selection pill (white) – flush left when Ōlelo, flush right when English */}
          <div style={{
            position: 'absolute',
            top: '0.2rem',
            left: isEnglish ? '50%' : '0.2rem',
            width: 'calc(50% - 0.2rem)',
            height: 'calc(100% - 0.4rem)',
            background: '#FFFCF9',
            borderRadius: 8,
            outline: '1px #D4CFCD solid',
            transition: 'left 180ms ease'
          }} />

          {/* Fixed labels - ŌLELO / ENGLISH uppercase; selected = dark on white, unselected = white on green */}
          <div style={{
            position: 'relative',
            zIndex: 1,
            width: '50%',
            textAlign: 'center',
            color: !isEnglish ? '#4D4D4D' : '#FFFFFF',
            fontSize: '1.05rem',
            fontFamily: 'Open Sans',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.02em'
          }}>
            {String(t('olelo', language)).toUpperCase()}
          </div>
          <div style={{
            position: 'relative',
            zIndex: 1,
            width: '50%',
            textAlign: 'center',
            color: isEnglish ? '#4D4D4D' : '#FFFFFF',
            fontSize: '1.05rem',
            fontFamily: 'Open Sans',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.02em'
          }}>
            {String(t('english', language)).toUpperCase()}
          </div>
        </div>

        {/* Home Icon - white fill inside, white icon on gradient */}
        <div 
          data-color="Green" 
          data-icon-side="Off" 
          data-size="Large" 
          data-status="Default" 
          data-type="Tertiary" 
          style={{
            width: '2.5rem', 
            height: '2.5rem', 
            minWidth: '2.5rem',
            minHeight: '2.5rem',
            borderRadius: '50%', 
            justifyContent: 'center', 
            alignItems: 'center', 
            display: 'flex',
            cursor: 'pointer',
            flexShrink: 0
          }}
          onClick={() => navigate('/dashboard')}
        >
          <img
            src="/assets/home_icon.png"
            alt="Home"
            style={{ width: '1.5rem', height: '1.5rem', objectFit: 'contain' }}
          />
        </div>

        {/* Profile icon – click opens dropdown with Logout */}
        <div ref={profileRef} style={{ position: 'relative' }}>
          <div 
            data-state="Default" 
            onClick={() => setProfileOpen((o) => !o)}
            style={{
              width: '2.5rem', 
              height: '2.5rem', 
              minWidth: '2.5rem',
              minHeight: '2.5rem',
              position: 'relative', 
              background: '#E5FFD0', 
              borderRadius: '50%', 
              outline: '1px solid rgba(0,0,0,0.12)', 
              outlineOffset: '-1px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <div style={{
              textAlign: 'center', 
              color: '#4F4C4A', 
              fontSize: '1.5rem', 
              fontFamily: 'Open Sans', 
              fontWeight: '500', 
              wordWrap: 'break-word',
              lineHeight: '1.5rem',
              margin: 0,
              padding: 0
            }}>
              {getUserInitial()}
            </div>
          </div>
          {profileOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '0.25rem',
                background: '#FFFCF9',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                border: '1px solid #E2E3E9',
                minWidth: '8rem',
                zIndex: 1000,
                overflow: 'hidden'
              }}
            >
              <button
                type="button"
                onClick={handleLogout}
                style={{
                  width: '100%',
                  padding: '0.6rem 1rem',
                  border: 'none',
                  background: 'transparent',
                  color: '#0D5449',
                  fontSize: '0.9rem',
                  fontFamily: 'Open Sans',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#E5FFD0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {t('logout', language)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NavBar;
